import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { fetchStoreInwardHistoryThunk } from "../../../store/slices/warehouseSlice";
import PaginationControls from "../../../utils/PaginationControls";

interface MaterialHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string | null;
  materialName: string | null;
  // New prop to trigger the edit modal from the parent
  onEditInward: (inwardId: string, prefillData: any) => void;
}

const MaterialHistoryModal: React.FC<MaterialHistoryModalProps> = ({
  isOpen,
  onClose,
  productId,
  materialName,
  onEditInward,
}) => {
  const dispatch = useDispatch<any>();
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const { storeInwardHistory, totalHistoryItems, totalHistoryPages, loading } =
    useSelector((state: RootState) => state.warehouse);

  // Admin Check
  const user = useSelector((state: RootState) => state.auth.userName);
  const isPrevilaged =
    user?.role === "admin" ||
    user?.role === "factory" ||
    user?.role === "store";

  useEffect(() => {
    if (isOpen && productId) {
      dispatch(
        fetchStoreInwardHistoryThunk({ productId, page: currentPage, limit }),
      );
    }
  }, [isOpen, productId, currentPage, dispatch]);

  // Helper to format date for HTML date inputs (YYYY-MM-DD)
  const formatDateForInput = (dateStr: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toISOString().split("T")[0];
  };

  // Inside MaterialHistoryModal.tsx

  const handleEditClick = (historyItem: any) => {
    const prefillData = {
      travelCost: (historyItem.travelCost || 0).toString(),
      otherCost: (historyItem.otherCost || 0).toString(),
      items: [
        {
          productId: historyItem.productId?._id || "",
          productName: historyItem.productId?.name || "",
          quantity: (historyItem.quantity?.value || 0).toString(),
          unit: historyItem.quantity?.unit || "",
          priceType: historyItem.unitPrice ? "unit" : "total",
          unitPrice: (historyItem.unitPrice || "").toString(),
          totalPrice: (historyItem.totalPrice || 0).toString(),
          vendorId: historyItem.vendorId?._id || "",
          warehouseId: historyItem.warehouseId?._id || "",
          billNumber: historyItem.billNumber || "",
          remark: historyItem.remark || "",
          orderedDate: formatDateForInput(historyItem.orderedDate),
          receivedDate: formatDateForInput(historyItem.receivedDate),
        },
      ],
    };

    onEditInward(historyItem._id, prefillData);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-gray-900/60 p-2 backdrop-blur-sm transition-opacity sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="material-history-title"
    >
      <div className="flex max-h-[96vh] w-full max-w-7xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-gray-200 animate-in fade-in zoom-in-95 duration-200 sm:max-h-[90vh] sm:rounded-2xl">
        <div className="flex flex-col gap-3 border-b border-gray-100 bg-gradient-to-r from-blue-50/80 to-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 sm:py-5">
          <div>
            <h2
              id="material-history-title"
              className="flex items-center gap-2 text-lg font-bold text-gray-900 sm:text-xl"
            >
              <span className="text-xl sm:text-2xl">📜</span> Inward History
            </h2>
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-500 sm:gap-2 sm:text-sm">
              <span>Viewing History for</span>
              <span className="inline-flex max-w-full items-center truncate rounded-md border border-blue-200 bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 shadow-sm sm:px-2.5 sm:py-0.5 sm:text-sm">
                {materialName || "Selected Material"}
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close material history"
            className="self-end rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:self-auto"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="min-h-0 min-w-0 flex-grow overflow-y-auto bg-gray-50/30 p-3 sm:p-6">
          <div className="space-y-3 xl:hidden">
            {loading && storeInwardHistory.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm">
                <div className="flex flex-col items-center gap-3">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                  Fetching batch history...
                </div>
              </div>
            ) : storeInwardHistory.length === 0 ? (
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-500 shadow-sm">
                No inward history found for this material.
              </div>
            ) : (
              storeInwardHistory.map((historyItem: any) => {
                const totalQty = historyItem.quantity?.value || 0;
                const unit = historyItem.quantity?.unit || "";
                const travelCost = historyItem.travelCost || 0;
                const otherCost = historyItem.otherCost || 0;
                const materialCost = historyItem.totalPrice || 0;
                const totalCost = travelCost + otherCost + materialCost;
                const rate = totalQty > 0 ? totalCost / totalQty : 0;
                const createdBy =
                  historyItem.createdBy?.username || "Unknown User";

                return (
                  <article
                    key={historyItem._id}
                    className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {historyItem.vendorId?.name || "Unknown Vendor"}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {new Date(
                            historyItem.receivedDate || historyItem.createdAt,
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      {isPrevilaged && (
                        <button
                          onClick={() => handleEditClick(historyItem)}
                          className="shrink-0 rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          title="Edit Inward Entry"
                          aria-label="Edit inward entry"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                        </button>
                      )}
                    </div>

                    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                      <div>
                        <dt className="text-gray-500">Created by</dt>
                        <dd className="mt-0.5 truncate font-medium text-gray-800">
                          {createdBy}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Bill no</dt>
                        <dd className="mt-0.5 truncate font-medium text-gray-800">
                          {historyItem.billNumber || "-"}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Inward quantity</dt>
                        <dd className="mt-0.5 font-medium text-gray-800">
                          {totalQty.toFixed(2)} {unit}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Rate</dt>
                        <dd className="mt-0.5 font-semibold text-indigo-600">
                          ₹
                          {rate.toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })}
                          <span className="font-normal text-gray-400">
                            /{unit}
                          </span>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Travel cost</dt>
                        <dd className="mt-0.5 font-medium text-gray-800">
                          ₹{travelCost.toLocaleString("en-IN")}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-gray-500">Other cost</dt>
                        <dd className="mt-0.5 font-medium text-gray-800">
                          ₹{otherCost.toLocaleString("en-IN")}
                        </dd>
                      </div>
                      <div className="col-span-2 rounded-md bg-gray-50 px-3 py-2">
                        <dt className="text-gray-500">Material cost</dt>
                        <dd className="mt-0.5 font-semibold text-gray-900">
                          ₹{materialCost.toLocaleString("en-IN")}
                        </dd>
                      </div>
                    </dl>
                  </article>
                );
              })
            )}
          </div>

          <div className="hidden min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm sm:rounded-xl xl:block">
            <div className="min-w-0 overflow-x-auto">
              <table className="w-full min-w-full table-auto divide-y divide-gray-200 text-xs sm:text-sm md:min-w-[1100px]">
                <thead className="bg-gray-50/90">
                  <tr>
                    <th className="sticky left-0 z-10 bg-gray-50/90 px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 sm:px-6 sm:py-4">
                      Date Received
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 sm:px-6 sm:py-4">
                      Vendor
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 sm:px-6 sm:py-4">
                      Created By
                    </th>
                    <th className="hidden px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 sm:table-cell sm:px-6 sm:py-4">
                      Bill No
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 sm:px-6 sm:py-4">
                      Inward QTY
                    </th>
                    <th className="hidden px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 sm:table-cell sm:px-6 sm:py-4">
                      Travel Cost
                    </th>
                    <th className="hidden px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 lg:table-cell sm:px-6 sm:py-4">
                      Other Cost
                    </th>
                    <th className="hidden px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 sm:table-cell sm:px-6 sm:py-4">
                      Material Cost
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 sm:px-6 sm:py-4">
                      Rate
                    </th>
                    {isPrevilaged && (
                      <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-600 sm:px-6 sm:py-4">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {loading && storeInwardHistory.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isPrevilaged ? 10 : 9}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="animate-pulse flex flex-col items-center gap-3">
                          <div className="h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                          Fetching batch history...
                        </div>
                      </td>
                    </tr>
                  ) : storeInwardHistory.length === 0 ? (
                    <tr>
                      <td
                        colSpan={isPrevilaged ? 10 : 9}
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        No inward history found for this material.
                      </td>
                    </tr>
                  ) : (
                    storeInwardHistory.map((historyItem: any) => {
                      const totalQty = historyItem.quantity?.value || 0;
                      const unit = historyItem.quantity?.unit || "";
                      const travelCost = historyItem.travelCost || 0;
                      const otherCost = historyItem.otherCost || 0;
                      const materialCost = historyItem.totalPrice || 0;
                      const totalCost = travelCost + otherCost + materialCost;
                      const rate = totalQty > 0 ? totalCost / totalQty : 0;
                      const createdBy =
                        historyItem.createdBy?.username || "Unknown User";
                      return (
                        <tr
                          key={historyItem._id}
                          className="hover:bg-blue-50/30 transition-colors group"
                        >
                          <td className="sticky left-0 z-[1] whitespace-nowrap bg-white px-3 py-3 font-medium text-gray-900 group-hover:bg-blue-50/30 sm:px-6 sm:py-4">
                            {new Date(
                              historyItem.receivedDate || historyItem.createdAt,
                            ).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          </td>
                          <td className="max-w-[120px] truncate px-3 py-3 font-medium text-gray-900 sm:max-w-none sm:whitespace-nowrap sm:px-6 sm:py-4">
                            {historyItem.vendorId?.name || "Unknown Vendor"}
                          </td>
                          <td className="max-w-[110px] truncate px-3 py-3 text-gray-700 sm:max-w-none sm:whitespace-nowrap sm:px-6 sm:py-4">
                            {createdBy}
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-3 text-gray-500 sm:table-cell sm:px-6 sm:py-4">
                            {historyItem.billNumber || "-"}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-right text-gray-700 sm:px-6 sm:py-4">
                            {totalQty.toFixed(2)}{" "}
                            <span className="text-gray-400 text-xs">
                              {unit}
                            </span>
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-3 text-right text-gray-700 sm:table-cell sm:px-6 sm:py-4">
                            ₹{travelCost.toLocaleString("en-IN")}
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-3 text-right text-gray-700 lg:table-cell sm:px-6 sm:py-4">
                            ₹{otherCost.toLocaleString("en-IN")}
                          </td>
                          <td className="hidden whitespace-nowrap px-3 py-3 text-right font-medium text-gray-900 sm:table-cell sm:px-6 sm:py-4">
                            ₹{materialCost.toLocaleString("en-IN")}
                          </td>
                          <td className="whitespace-nowrap px-3 py-3 text-right font-medium text-indigo-600 sm:px-6 sm:py-4">
                            ₹
                            {rate.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                            <span className="text-gray-400 text-xs font-normal">
                              /{unit}
                            </span>
                          </td>
                          {isPrevilaged && (
                            <td className="whitespace-nowrap px-3 py-3 text-right sm:px-6 sm:py-4">
                              <button
                                onClick={() => handleEditClick(historyItem)}
                                className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg transition-all"
                                title="Edit Inward Entry"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                  />
                                </svg>
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="z-10 flex flex-col items-stretch gap-3 border-t border-gray-100 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-4">
          <div className="text-center text-xs font-medium text-gray-500 sm:text-left sm:text-sm">
            Showing latest batches
          </div>
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalHistoryPages}
            onPageChange={setCurrentPage}
            isLoading={loading}
            totalItems={totalHistoryItems}
          />
        </div>
      </div>
    </div>
  );
};

export default MaterialHistoryModal;
